import {
  Box,
  Flex,
  Grid,
  type GridProps,
  Spinner,
  Stack,
  useBoolean,
} from "@chakra-ui/react";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToFirstScrollableAncestor,
  restrictToHorizontalAxis,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { t } from "@lingui/macro";
import {
  PlanningDailyViewWhereColumn,
  PlanningOrderTypeEnum,
  usePlanningFilterOptionsQuery,
  useUpdatePlanningUsersOrderMutation,
} from "@src/__generated__/urql-graphql";
import { SortableUserHeader } from "@src/components/modules/resource-planning/SortableUserHeader";
import { LoadingOverlay } from "@src/components/ui-kit/LoadingOverlay/LoadingOverlay";
import {
  COLUMN_HEADER_HEIGHT,
  COLUMN_WIDTH,
  DAY_STRIP_WIDTH,
} from "@src/constants/planning";
import { PlanningViewTypeEnum } from "@src/stores/models/WorkspaceSettings";
import type { ResourcePlanningDayStore } from "@src/stores/ResourcePlanningDayStore";
import type { ResourcePlanningWeekStore } from "@src/stores/ResourcePlanningWeekStore";
import { withCan } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import useIntersectionObserver from "@src/utils/hooks/useIntersectionObserver";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { pxToNumber } from "@src/utils/pxToNumber";
import { cloneDeep } from "lodash";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { ResourcePlanningDndView } from "../ResourcePlanningDndView";
import { ResourcePlanningFilterRow } from "../ResourcePlanningFilterRow";
import { AllocationsColumnDay } from "./AllocationsColumn";
import { DayStrip } from "./DayStrip";

export const ResourcePlanningDailyViewStoreContext = createContext<
  ResourcePlanningDayStore | ResourcePlanningWeekStore
>(null as unknown as ResourcePlanningDayStore | ResourcePlanningWeekStore);

export const useResourcePlanningDailyViewStore = () =>
  useContext(ResourcePlanningDailyViewStoreContext);

export const ResourcePlanningDayPage = withCan(
  { do: "planning_read_all" },
  observer(function ResourcePlanningDayPage() {
    const { resourcePlanningDayStore, workspaceStore } = useStore();
    const { isMobile } = useScreenType();
    const [initialLoad, { off }] = useBoolean(true);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const mouseSensor = useSensor(MouseSensor);
    const pointerSensor = useSensor(PointerSensor);
    const touchSensor = useSensor(TouchSensor);
    const keyboardSensor = useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    });
    const sensors = useSensors(
      mouseSensor,
      touchSensor,
      keyboardSensor,
      pointerSensor,
    );
    const templateColumns: GridProps["templateColumns"] = useMemo(
      () =>
        // eslint-disable-next-line lingui/no-unlocalized-strings
        `${DAY_STRIP_WIDTH} repeat(${resourcePlanningDayStore.data.length}, minmax(${COLUMN_WIDTH}, 425px))`,
      [resourcePlanningDayStore.data.length],
    );
    const ids = useMemo(
      () => resourcePlanningDayStore.data.map((item) => item.user.id),
      [resourcePlanningDayStore.data],
    );

    const [{ data: filterOptionsData }] = usePlanningFilterOptionsQuery();
    const [_, reorderUsers] = useUpdatePlanningUsersOrderMutation();

    useEffect(() => {
      if (!filterOptionsData) return;

      resourcePlanningDayStore.setFilterOptions(filterOptionsData);
    }, [filterOptionsData]);

    const fetchAndScrollTo = async () => {
      await resourcePlanningDayStore.fetchDailyView();

      setTimeout(() => {
        let dateToScrollTo: string;
        let top: number;

        if (!initialLoad) {
          dateToScrollTo = resourcePlanningDayStore.selectedDate.toDateString();
          top =
            workspaceStore.settings?.default_planning_view_settings
              ?.view_type === PlanningViewTypeEnum.WEEK_START
              ? (document.getElementById(dateToScrollTo)?.offsetTop ?? 0)
              : 0;
        } else {
          dateToScrollTo = new Date().toDateString();
          top = document.getElementById(dateToScrollTo)?.offsetTop ?? 0;

          off();
        }

        scrollContainerRef.current?.scrollTo({
          behavior: "smooth",
          top: top - pxToNumber(COLUMN_HEADER_HEIGHT),
        });
      }, 250);
    };

    const onUserDragStart = () => {
      resourcePlanningDayStore.clonedData = cloneDeep(
        toJS(resourcePlanningDayStore.data),
      );
    };

    const onUserDragEnd = async ({ active, over }: DragEndEvent) => {
      if (!active.id || !over?.id || active.id === over.id) return;

      const oldIndex = resourcePlanningDayStore.data.findIndex(
        (userItem) => userItem.user.id === active.id,
      );
      const newIndex = resourcePlanningDayStore.data.findIndex(
        (userItem) => userItem.user.id === over.id,
      );
      resourcePlanningDayStore.data = arrayMove(
        resourcePlanningDayStore.data,
        oldIndex,
        newIndex,
      );

      const user_ids = resourcePlanningDayStore.data.map(
        (item) => item.user.id,
      );
      const teamFilter = resourcePlanningDayStore.where.filtersByColumn.get(
        PlanningDailyViewWhereColumn.TeamId,
      );
      const type = teamFilter?.value[0]
        ? PlanningOrderTypeEnum.Team
        : PlanningOrderTypeEnum.Default;
      const model_id = teamFilter?.value[0] ?? undefined;

      const { data } = await reorderUsers({
        user_ids,
        type,
        model_id,
      });
      if (!data?.updatePlanningUsersOrder) {
        resourcePlanningDayStore.revertChanges();
        return;
      }
      resourcePlanningDayStore.appStore.UIStore.toast({
        title: t`Order successfully updated`,
        status: "success",
      });
      resourcePlanningDayStore.clonedData = undefined;
    };

    useEffect(() => {
      resourcePlanningDayStore.reset();
      fetchAndScrollTo();
    }, [resourcePlanningDayStore.selectedDate]);

    useIntersectionObserver({
      target: loadMoreRef,
      threshold: 0.01,
      onIntersect: () => {
        if (resourcePlanningDayStore.fetcher.isLoading.value) return;
        if (!resourcePlanningDayStore.canLoadMore) return;
        resourcePlanningDayStore.fetchDailyView({
          backgroundUpdate: true,
          loadForAllUsers: false,
        });
      },
    });

    return (
      <ResourcePlanningDailyViewStoreContext.Provider
        value={resourcePlanningDayStore}
      >
        <Stack
          pos="relative"
          direction="column"
          maxW="full"
          maxH="full"
          bg="white"
          borderColor="grey.50"
          borderTopWidth="1px"
        >
          <ResourcePlanningFilterRow
            pl={isMobile ? undefined : DAY_STRIP_WIDTH}
          />

          {!!resourcePlanningDayStore.data && (
            <LoadingOverlay
              ref={scrollContainerRef}
              overflow="scroll"
              position="absolute"
              isLoading={resourcePlanningDayStore.fetcher.isLoading.value}
            >
              <Grid
                pos="sticky"
                zIndex="12"
                top="0"
                rowGap="6"
                templateColumns={templateColumns}
                minW="fit-content"
                bg="white"
              >
                <Box
                  zIndex="11"
                  top="0"
                  left="0"
                  h={COLUMN_HEADER_HEIGHT}
                  bg="white"
                  borderTopWidth="1px"
                  borderTopColor="grey.100"
                  borderRightWidth="1px"
                  borderRightColor="grey.50"
                  borderBottomWidth="1px"
                ></Box>
                <DndContext
                  sensors={sensors}
                  onDragStart={onUserDragStart}
                  autoScroll={{ threshold: { x: 0.2, y: 0 } }}
                  onDragEnd={onUserDragEnd}
                  modifiers={[
                    restrictToHorizontalAxis,
                    restrictToFirstScrollableAncestor,
                  ]}
                >
                  <SortableContext
                    id="users"
                    items={ids}
                    strategy={horizontalListSortingStrategy}
                  >
                    {resourcePlanningDayStore.data.map((item) => (
                      <SortableUserHeader key={item.user.id} user={item.user} />
                    ))}
                  </SortableContext>
                </DndContext>
              </Grid>
              <Stack flexDir="row">
                <ResourcePlanningDndView>
                  <Grid
                    templateColumns={templateColumns}
                    minW="fit-content"
                    bg="grey.50"
                  >
                    {resourcePlanningDayStore.daysOfSelectedInterval.map(
                      (day, dayIndex) => (
                        <Fragment key={day.toISOString()}>
                          <DayStrip day={day} />
                          {resourcePlanningDayStore.data.map(
                            ({ user, items }) => (
                              <AllocationsColumnDay
                                key={user.id}
                                user={user}
                                day={items[dayIndex]}
                              />
                            ),
                          )}
                        </Fragment>
                      ),
                    )}
                  </Grid>
                </ResourcePlanningDndView>
                {resourcePlanningDayStore.canLoadMore && (
                  <Flex justify="center" w={COLUMN_WIDTH} minW={COLUMN_WIDTH}>
                    <Box ref={loadMoreRef} pos="relative">
                      <Spinner
                        pos="sticky"
                        top="40dvh"
                        right="0"
                        left="0"
                        size="lg"
                      />
                    </Box>
                  </Flex>
                )}
              </Stack>
            </LoadingOverlay>
          )}
        </Stack>
      </ResourcePlanningDailyViewStoreContext.Provider>
    );
  }),
);
